import React, { useState, useEffect } from 'react';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const SwissDot = ({ size = 13 }) => (
  <span style={{ display: 'inline-flex', width: size, height: size, verticalAlign: 'middle' }}>
    <svg viewBox="0 0 32 32" style={{ width: '100%', height: '100%' }}><rect width="32" height="32" rx="6" fill="#E2231A" /><path d="M13.5 6.5H18.5V13.5H25.5V18.5H18.5V25.5H13.5V18.5H6.5V13.5H13.5Z" fill="#fff" /></svg>
  </span>
);

/**
 * Page publique de questionnaire (hors authentification).
 * Lien : /#/questionnaire/:token — API GET/POST /q/:token.
 * Le candidat répond aux écarts ; les réponses sont auto-scorées au retour.
 */
export default function QuestionnairePage({ token }) {
  const [state, setState] = useState('loading'); // loading | ready | done | invalid
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/q/${encodeURIComponent(token)}`);
        if (r.status === 404 || r.status === 410) { if (!cancelled) setState('invalid'); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const d = j.data || j;
        if (!cancelled) {
          setData(d);
          if (d.already_answered || (d.status || '').match(/complet|submitted|done|répondu|answered/i)) setState('done');
          else setState('ready');
        }
      } catch (e) { if (!cancelled) setState('invalid'); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const questions = (data?.questions || data?.gaps || data?.items || []).map((q, i) => ({
    id: q.id ?? q.key ?? q.gap ?? i,
    text: q.question || q.text || q.label || q.titre || `Question ${i + 1}`,
    hint: q.hint || q.detail || '',
  }));
  const candidat = data?.candidat_prenom || data?.candidate?.nom || data?.candidate?.name || data?.candidat || '';
  const besoin = data?.besoin_titre || data?.besoin?.titre || data?.besoin?.title || data?.poste || '';
  const client = data?.besoin_client || data?.besoin?.client || data?.client || '';

  const submit = async () => {
    const payload = questions.map(q => ({ id: q.id, answer: (answers[q.id] || '').trim() })).filter(a => a.answer);
    if (!payload.length) { setError('Merci de répondre à au moins une question.'); return; }
    setError(''); setSubmitting(true);
    try {
      const r = await fetch(`${BASE}/q/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers: payload }),
      });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`HTTP ${r.status}${t ? ' — ' + t.slice(0, 140) : ''}`); }
      setState('done');
    } catch (e) {
      console.error('Envoi questionnaire échoué:', e);
      setError(`Envoi impossible : ${e.message || e}. Réessayez dans un instant.`);
    } finally { setSubmitting(false); }
  };

  const page = (children) => (
    <div style={{ minHeight: '100vh', background: '#f2f3f5', color: '#0b0c11', fontFamily: "'IBM Plex Sans', system-ui, sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 16px' }}>
      <div style={{ width: '100%', maxWidth: '620px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '22px' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: 800, letterSpacing: '-0.03em' }}>erpia</span>
          <SwissDot />
        </div>
        {children}
        <div style={{ marginTop: '26px', fontSize: '11px', color: '#8a8f9c', textAlign: 'center' }}>Vos réponses sont transmises de façon sécurisée au recruteur.</div>
      </div>
    </div>
  );

  const card = { background: '#fff', border: '1px solid #e5e7ec', borderRadius: '16px', padding: 'clamp(24px,4vw,40px)' };

  if (state === 'loading') return page(<div style={{ ...card, textAlign: 'center', color: '#6a6f7b' }}><BI icon="arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> Chargement…<style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>);

  if (state === 'invalid') return page(
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(226,35,26,0.10)', color: '#E2231A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '22px' }}><BI icon="exclamation-triangle" /></div>
      <div style={{ fontSize: '17px', fontWeight: 700 }}>Lien invalide ou expiré</div>
      <div style={{ marginTop: '7px', fontSize: '13.5px', color: '#6a6f7b' }}>Ce questionnaire n'est plus disponible. Contactez votre interlocuteur pour un nouveau lien.</div>
    </div>
  );

  if (state === 'done') return page(
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(45,200,110,0.16)', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '24px' }}><BI icon="check-lg" /></div>
      <div style={{ fontSize: '18px', fontWeight: 700 }}>Merci{candidat ? `, ${candidat}` : ''} !</div>
      <div style={{ marginTop: '7px', fontSize: '13.5px', color: '#6a6f7b' }}>Vos réponses ont bien été enregistrées. Le recruteur reviendra vers vous.</div>
    </div>
  );

  return page(<>
    <div style={{ ...card }}>
      <div style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8a8f9c' }}>Questionnaire</div>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '4px' }}>{besoin || 'Quelques précisions'}<span style={{ color: '#E2231A' }}>.</span></div>
      {(candidat || client) && <div style={{ marginTop: '5px', fontSize: '13.5px', color: '#6a6f7b' }}>{[candidat, client].filter(Boolean).join(' · ')}</div>}
      <div style={{ marginTop: '10px', fontSize: '13.5px', color: '#4a4f5b', lineHeight: 1.55 }}>Bonjour{candidat ? ` ${candidat}` : ''}, dans le cadre de votre candidature{besoin ? ` au poste de ${besoin}` : ''}, merci de préciser les points ci-dessous : vos réponses aideront à mieux évaluer votre profil.</div>

      <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {questions.length === 0 && <div style={{ fontSize: '13.5px', color: '#8a8f9c' }}>Aucune question. Vous pouvez fermer cette page.</div>}
        {questions.map((q, i) => (
          <div key={q.id}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '7px' }}><span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#E2231A', marginRight: '7px' }}>{String(i + 1).padStart(2, '0')}</span>{q.text}</label>
            {q.hint && <div style={{ fontSize: '12px', color: '#8a8f9c', margin: '-3px 0 7px' }}>{q.hint}</div>}
            <textarea value={answers[q.id] || ''} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} rows={3}
              placeholder="Votre réponse…" style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: '10px', border: '1px solid #d8dbe1', background: '#fff', color: '#0b0c11', fontFamily: 'inherit', fontSize: '14px', lineHeight: 1.5, resize: 'vertical', outline: 'none' }} />
          </div>
        ))}
      </div>

      {error && <div style={{ marginTop: '14px', fontSize: '12.5px', color: '#E2231A', display: 'flex', alignItems: 'center', gap: '6px' }}><BI icon="exclamation-circle" /> {error}</div>}

      {questions.length > 0 && (
        <button onClick={submit} disabled={submitting} style={{ marginTop: '22px', width: '100%', padding: '13px', borderRadius: '11px', border: 'none', background: '#E2231A', color: '#fff', fontFamily: 'inherit', fontSize: '14.5px', fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
          {submitting ? 'Envoi…' : 'Envoyer mes réponses'}
        </button>
      )}
    </div>
  </>);
}
